import {
  BALANCE,
  applyEstimate,
  reduceSamples,
  sampleRoundTrip,
  type ClockEstimate,
  type ClockPing,
  type ClockPong,
  type ClockSample,
  type Timestamp,
} from '@2k48/shared';
import { metrics } from '../game/debug/metrics';

/**
 * The client half of the clock handshake.
 *
 * Everything the server schedules is announced as an absolute server timestamp.
 * To act on one, the client needs to know how far its own clock is from the
 * server's — that is all this class produces.
 *
 * Two details that are easy to get wrong and expensive to fix later:
 *
 *  - **Local time comes from `performance.now()`, not `Date.now()`.** The wall
 *    clock can be stepped by NTP or by the user mid-match, which would show up
 *    as a sudden offset error. `performance.timeOrigin + performance.now()` is
 *    monotonic and still epoch-aligned.
 *
 *  - **The handshake sends several probes and keeps the fastest.** A single
 *    round trip on mobile is as likely to be a 400ms outlier as a 40ms
 *    baseline, and the offset error is roughly half the asymmetry.
 */
export class SyncedClock {
  private samples: ClockSample[] = [];
  private pending = new Map<number, Timestamp>();
  private estimate: ClockEstimate | null = null;
  private nextId = 1;
  private resyncHandle = 0;
  private handshakeHandle = 0;
  private resolveHandshake: ((estimate: ClockEstimate) => void) | null = null;

  constructor(private readonly send: (ping: ClockPing) => void) {}

  /** Monotonic, epoch-aligned local time. */
  static clientNow(): Timestamp {
    return performance.timeOrigin + performance.now();
  }

  /** Best guess at the server's clock, right now. */
  serverNow(): Timestamp {
    return SyncedClock.clientNow() + (this.estimate?.offset ?? 0);
  }

  /** Milliseconds from now until an absolute server timestamp. Can be negative. */
  until(serverTime: Timestamp): number {
    return serverTime - this.serverNow();
  }

  get current(): ClockEstimate | null {
    return this.estimate;
  }

  get synced(): boolean {
    return this.estimate !== null;
  }

  /**
   * Fire the initial burst of probes. Resolves once enough have come back to
   * trust the offset; the caller should await it before acting on any phase
   * deadline, or it will schedule against an offset of zero.
   */
  handshake(): Promise<ClockEstimate> {
    this.samples = [];

    return new Promise<ClockEstimate>((resolve) => {
      this.resolveHandshake = resolve;

      let sent = 0;
      const fire = (): void => {
        this.probe();
        sent += 1;
        if (sent >= BALANCE.clock.handshakeSamples) {
          window.clearInterval(this.handshakeHandle);
          this.handshakeHandle = 0;
          this.startResyncing();
          return;
        }
      };

      fire();
      this.handshakeHandle = window.setInterval(fire, BALANCE.clock.handshakeIntervalMs);
    });
  }

  private probe(): void {
    const id = this.nextId++;
    const sentAt = SyncedClock.clientNow();
    this.pending.set(id, sentAt);
    this.send({ id, clientSentAt: sentAt });
  }

  /** Feed a reply back in. Wire this to the CLOCK_PONG message. */
  onPong(pong: ClockPong): void {
    const sentAt = this.pending.get(pong.id);
    if (sentAt === undefined) return;
    this.pending.delete(pong.id);

    this.samples.push(sampleRoundTrip(sentAt, pong.serverTime, SyncedClock.clientNow()));
    // A rolling window: an estimate from probes two minutes old is worse than
    // one from three probes taken now.
    if (this.samples.length > BALANCE.clock.handshakeSamples * 2) this.samples.shift();

    const next = reduceSamples(this.samples);
    if (!next) return;

    this.estimate = applyEstimate(this.estimate, next);
    metrics.setClock(this.estimate.offset, this.estimate.rtt, this.estimate.jitter);

    if (this.resolveHandshake && this.samples.length >= BALANCE.clock.keepBestSamples) {
      this.resolveHandshake(this.estimate);
      this.resolveHandshake = null;
    }
  }

  /**
   * Keep re-measuring for the life of the connection. Clocks drift, networks
   * change under a moving phone, and a match can outlast the accuracy of a
   * handshake taken at join time.
   */
  private startResyncing(): void {
    if (this.resyncHandle !== 0) return;
    this.resyncHandle = window.setInterval(() => this.probe(), BALANCE.clock.resyncIntervalMs);
  }

  destroy(): void {
    if (this.handshakeHandle !== 0) window.clearInterval(this.handshakeHandle);
    if (this.resyncHandle !== 0) window.clearInterval(this.resyncHandle);
    this.handshakeHandle = 0;
    this.resyncHandle = 0;
    this.pending.clear();
    this.resolveHandshake = null;
  }
}
